import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Overline, CTASecondary } from "@/components/primitives";
import { useEvidence } from "@/lib/useEvidence";
import { CLIENTS } from "@/data/content";

export function EvidenceCard({ r, index = 0 }) {
  return (
    <Reveal delay={index * 0.05} className="border-r border-b hairline">
      <Link
        to={`/evidence/${r.slug}`}
        data-testid={`evidence-card-${r.slug}`}
        className="group block h-full bg-warm hover:bg-mist/40 transition-colors duration-300"
      >
        {r.image ? (
          <div className="relative overflow-hidden aspect-[4/3] bg-navy/5">
            <img src={r.image} alt={`${r.org} — ${r.platform}`} loading="lazy"
              className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-[filter] duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          </div>
        ) : (
          <div className="aspect-[4/3] flex items-center px-7 bg-navy text-warm">
            <span className="font-heading font-light text-2xl leading-snug">{r.platform}</span>
          </div>
        )}
        <div className="px-6 py-6">
          <span className="overline text-gold">{r.engagement_type}</span>
          <h3 className="mt-3 font-heading font-light text-navy text-xl leading-tight">{r.org}</h3>
          {r.person && <p className="mt-1 text-sm text-ink/55 font-body">{r.person} · {r.role}</p>}
          <div className="mt-4 flex items-center justify-between text-navy/60">
            <span className="text-xs uppercase tracking-widest">{r.platform || "On record"}</span>
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </div>
        </div>
      </Link>
    </Reveal>
  );
}

export default function Experience() {
  const { records } = useEvidence(true);
  return (
    <section className="py-24 md:py-36 border-b hairline" data-testid="experience-section">
      <div className="mx-auto max-w-[1500px] px-6 md:px-10">
        <div className="max-w-3xl mb-14">
          <Reveal><Overline>06 — Experience</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-8 font-heading font-light text-navy tracking-tight text-4xl md:text-5xl leading-[1.05]">
              We've helped leaders and organizations enter the right rooms.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 text-lg leading-relaxed text-ink/70 font-body">
              From executive platforms and industry recognition to strategic visibility activations,
              our work has helped organizations and their leaders show up where credibility and
              business relationships are built.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-l border-t hairline">
          {records.map((r, i) => <EvidenceCard key={r.slug} r={r} index={i} />)}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
            <p className="text-sm text-ink/55 font-body max-w-xl">
              Selected from a broader portfolio of documented executive and organizational visibility engagements.
            </p>
            <CTASecondary to="/evidence" testId="explore-evidence-link">Explore Client Evidence</CTASecondary>
          </div>
        </Reveal>

        {/* Restrained logo wall */}
        <div className="mt-16 pt-16 border-t hairline">
          <Reveal><p className="overline text-navy/50 mb-8">Organizations we've supported</p></Reveal>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 border-l border-t hairline" data-testid="client-logo-wall">
            {CLIENTS.map((c, i) => (
              <Reveal key={c.name} delay={i * 0.03} className="border-r border-b hairline">
                <div className="group aspect-[3/2] flex items-center justify-center px-5 bg-warm hover:bg-mist/50 transition-colors duration-300">
                  <img src={c.logo} alt={c.name} loading="lazy"
                    className={`w-auto object-contain grayscale group-hover:grayscale-0 transition-[filter,opacity] duration-500 ${
                      c.dark ? "max-h-12 opacity-75 group-hover:opacity-100" : "max-h-9 opacity-50 group-hover:opacity-100"
                    }`} />
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="mt-6 text-xs leading-relaxed text-ink/40 font-body max-w-4xl">
              Logos represent documented visibility, recognition or engagement and do not necessarily
              indicate a current partnership, endorsement or affiliation unless expressly stated.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
